import React from "react";

/**
 * App-wide error boundary. Without this, any unhandled render/hook exception
 * unmounts the React root and leaves the page with only the CSS background
 * (dark theme) visible — which shows up as a "blank black screen" on mobile.
 *
 * Also captures the error into localStorage so field-testing teams can
 * retrieve the exact stack trace by visiting `/#diag` after the crash.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Persist so field teams can share the exact error afterwards.
    try {
      const payload = {
        message: String(error?.message || error),
        stack: String(error?.stack || ""),
        componentStack: String(info?.componentStack || ""),
        href: typeof window !== "undefined" ? window.location.href : "",
        ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
        at: new Date().toISOString(),
      };
      const key = "bt.lastError";
      localStorage.setItem(key, JSON.stringify(payload));
    } catch (_e) { /* ignore storage errors */ }
    // Also log to console so DevTools shows it.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  handleReload = () => {
    try { localStorage.removeItem("bt.lastError"); } catch (_e) {}
    window.location.reload();
  };

  handleCopy = () => {
    try {
      const raw = localStorage.getItem("bt.lastError") || "";
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(raw);
      } else {
        // Fallback for old iOS
        const ta = document.createElement("textarea");
        ta.value = raw;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
    } catch (_e) {}
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        data-testid="error-boundary"
        style={{
          minHeight: "100vh",
          background: "#0c1524",
          color: "#e2e8f0",
          padding: "24px",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        <div style={{ maxWidth: 520, margin: "10vh auto" }}>
          <div style={{ color: "#fbbf24", fontSize: 12, letterSpacing: 1.5, textTransform: "uppercase" }}>
            Something went wrong
          </div>
          <h1 style={{ fontSize: 28, margin: "8px 0 12px", lineHeight: 1.15 }}>
            The app hit an unexpected error.
          </h1>
          <p style={{ fontSize: 14, opacity: 0.8, marginBottom: 16 }}>
            Tap Reload to try again. If this keeps happening, tap “Copy details” and share it with your admin.
          </p>
          <pre
            style={{
              fontSize: 12,
              background: "#1e293b",
              border: "1px solid #334155",
              padding: 12,
              overflow: "auto",
              maxHeight: 220,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button
              onClick={this.handleReload}
              style={{
                background: "#fbbf24",
                color: "#0c1524",
                border: 0,
                padding: "12px 20px",
                fontWeight: 700,
                cursor: "pointer",
              }}
              data-testid="error-reload-btn"
            >
              Reload app
            </button>
            <button
              onClick={this.handleCopy}
              style={{
                background: "transparent",
                color: "#e2e8f0",
                border: "1px solid #334155",
                padding: "12px 20px",
                cursor: "pointer",
              }}
              data-testid="error-copy-btn"
            >
              Copy details
            </button>
          </div>
        </div>
      </div>
    );
  }
}
